import { useMemo } from "react"
import Select, {
  type TSelectOption,
} from "~/ui-rtk/components/ui/controls/Select"
import {
  useCompanyConnectorControllerGetCompanyConnectorsQuery,
  useCompanyConnectorControllerGetGa4PropertiesQuery,
} from "~/ui-rtk/api/companyConnectorApi"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import {
  REGIONAL_COUNTRIES,
  isRegionalCountry,
  normalizeGaPropertyId,
  type RegionalCountry,
} from "~/ui-rtk/constants/regional"

// The empty value clears the mapping: an unmapped property reports as 'Other'
// in the pipeline, exactly like a property that was never listed.
const COUNTRY_OPTIONS: TSelectOption[] = [
  { label: "Not mapped (Other)", value: "" },
  ...REGIONAL_COUNTRIES.map(country => ({ label: country, value: country })),
]

type CountryBreakoutGaMapProps = {
  /** GA4 property id (bare numeric) → country. */
  value: Record<string, RegionalCountry>
  onChange: (next: Record<string, RegionalCountry>) => void
}

/**
 * Per-GA4-property country picker. Lists the company's GA4 properties (from
 * the GA4 connector) with a USA / Canada select each; the map is owned by the
 * parent form via value/onChange. v1 of the regional pipeline attributes a
 * WHOLE property to one country (no visitor geo), which is what this encodes.
 */
export default function CountryBreakoutGaMap({
  value,
  onChange,
}: CountryBreakoutGaMapProps) {
  const { data: companyConnectors, isLoading: isLoadingConnectors } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery()

  // The properties endpoint takes the GA4 company-connector id. Prefer the
  // native connector, falling back to the Airbyte variant.
  const ga4Connector =
    companyConnectors?.find(
      connector => connector.serviceName === CONNECTORS_IDS.GA4,
    ) ??
    companyConnectors?.find(
      connector => connector.serviceName === CONNECTORS_IDS.GA4_AIRBYTE,
    )

  const {
    data: propertiesData,
    isLoading: isLoadingProperties,
    error: propertiesError,
  } = useCompanyConnectorControllerGetGa4PropertiesQuery(
    { id: ga4Connector?.id ?? "" },
    { skip: !ga4Connector?.id },
  )

  // Connector properties first; then any mapped id the connector no longer
  // returns (property removed or access lost), so a stale mapping can still
  // be cleared instead of silently steering GA metrics into a country.
  const rows = useMemo(() => {
    const listed = (propertiesData?.data ?? []).map(property => ({
      id: normalizeGaPropertyId(property.propertyId),
      label: `${property.propertyName} (${normalizeGaPropertyId(property.propertyId)})`,
    }))
    const listedIds = new Set(listed.map(row => row.id))
    const stale = Object.keys(value)
      .filter(id => !listedIds.has(id))
      .map(id => ({ id, label: `Unknown property (${id})` }))
    return [...listed, ...stale]
  }, [propertiesData, value])

  const handleSelect = (propertyId: string, country: string) => {
    const next = { ...value }
    if (isRegionalCountry(country)) {
      next[propertyId] = country
    } else {
      delete next[propertyId]
    }
    onChange(next)
  }

  // Both fetches gate their "missing" states: until the connector list is in,
  // `ga4Connector` is undefined for every company, which must not read as
  // "no connector".
  if (isLoadingConnectors || isLoadingProperties) {
    return <p className="py-3 text-sm text-cadet-blue">Loading...</p>
  }

  if (!ga4Connector) {
    return (
      <p className="py-3 text-sm text-cadet-blue">
        No Google Analytics 4 connector found for this company.
      </p>
    )
  }

  if (propertiesError) {
    return (
      <p className="py-3 text-sm text-states-error">
        Failed to load GA4 properties
      </p>
    )
  }

  if (rows.length === 0) {
    return (
      <p className="py-3 text-sm text-cadet-blue">
        No GA4 properties available.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {rows.map(row => (
        <div
          key={row.id}
          className="grid grid-cols-3 grid-rows-1 gap-8 items-center"
        >
          <label
            htmlFor={`ga-property-country-${row.id}`}
            className="col-span-2 text-sm"
          >
            {row.label}
          </label>
          <Select
            id={`ga-property-country-${row.id}`}
            options={COUNTRY_OPTIONS}
            value={value[row.id] ?? ""}
            onChange={event => handleSelect(row.id, event.target.value)}
          />
        </div>
      ))}
    </div>
  )
}
